/* eslint-disable no-underscore-dangle */

import { isActuatingGripper } from '@sb/integrations/utils';
import { STILL_GOING_CHECK_INTERVAL_MS } from '@sb/routine-runner/constants';
import { FailureKind } from '@sb/routine-runner/FailureKind';
import type { StepPlayArguments } from '@sb/routine-runner/Step/Step';
import Step from '@sb/routine-runner/Step/Step';
import { wait } from '@sb/utilities';

import {
  ActuateGripperArguments as Arguments,
  MISSING_DEVICE_MARKER,
} from './Arguments';
import { ActuateGripperVariables as Variables } from './Variables';

export class ActuateGripperStep extends Step<Arguments, Variables> {
  public static areSubstepsRequired = false;

  public static Arguments = Arguments;

  public static Variables = Variables;

  // the play loop knows to clean up after `stop()` is called
  private stillGoing: boolean = false;

  // while we're paused, we don't want to send another gripper command
  private isPaused: boolean = false;

  public initializeVariableState(): void {
    this.variables = {
      succeeded: false,
    };
  }

  public async _play({ fail }: StepPlayArguments): Promise<void> {
    const { gripperCommand, payload, retry, stopRoutineOnFailure, deviceID } =
      this.args;

    this.setVariable('succeeded', false);
    this.stillGoing = true;

    // Validate that an enabled gripper exists
    const equipmentState = this.routineContext.equipment.getEquipmentState();

    const enabledGripper = equipmentState.find((item) =>
      isActuatingGripper(item.kind),
    );

    if (enabledGripper == null) {
      return fail({
        failure: {
          kind: FailureKind.GripperFailure,
          message: `No enabled gripper found. Please enable the correct gripper in the Equipment Manager or through the Enable Gripper step.`,
        },
        failureReason: `No enabled gripper found. Please enable the correct gripper in the Equipment Manager or through the Enable Gripper step.`,
      });
    }

    // If deviceID is set, validate that the enabled gripper matches
    if (deviceID && deviceID !== MISSING_DEVICE_MARKER) {
      if (enabledGripper.id !== deviceID) {
        const enabledGripperDevice =
          this.routineContext.equipment.getDeviceById(enabledGripper.id);

        const selectedDevice =
          this.routineContext.equipment.getDeviceById(deviceID);

        const enabledGripperName = enabledGripperDevice?.getConfig().name;

        const selectedDeviceName = selectedDevice?.getConfig().name;

        const enabledGripperPart = enabledGripperName
          ? `"${enabledGripperName}"`
          : '';

        const selectedDevicePart = selectedDeviceName
          ? `"${selectedDeviceName}"`
          : '';

        const message = `The enabled gripper${enabledGripperPart ? ` ${enabledGripperPart}` : ''} does not match the selected gripper${selectedDevicePart ? ` ${selectedDevicePart}` : ''}. Please enable the correct gripper in the Equipment Manager.`;

        return fail({
          failure: {
            kind: FailureKind.GripperFailure,
            message,
          },
          failureReason: message,
        });
      }
    }

    // To start, this will basically just call `actuateGripper`.
    while (this.stillGoing) {
      // If `actuateGripper` gets interrupted by `pause()`'s `stopGripper`,
      // we want to loop until we've resumed, and we'll call `actuateGripper` again
      // when we resume.
      if (!this.isPaused) {
        try {
          await this.routineContext.actuateDevice(gripperCommand, { retry });

          // actuateDevice() returns normally when gripperStopped fires during
          // a pause. Don't treat an interrupted actuation as success — retry
          // on resume instead.
          if (this.isPaused) {
            continue;
          }

          if (payload) {
            await this.routineContext.setPayload(payload, this.routineFrame);
          }

          this.setVariable('succeeded', true);
        } catch (error) {
          if (this.isPaused) {
            continue;
          }

          if (!stopRoutineOnFailure) {
            break;
          }

          return fail({
            failure: {
              kind: FailureKind.GripperFailure,
              message: error.message,
            },
            failureReason: `Failed to actuate gripper during “Actuate Gripper” step: ${error.message}`,
            error,
          });
        }

        // If pause stopped it, we want to keep looping.
        // If pause didn't stop it, it either succeeded or `stop()` stopped it.
        // In either case, `play()` should now resolve.
        if (!this.isPaused) {
          break;
        }
      }

      await wait(STILL_GOING_CHECK_INTERVAL_MS);
    }

    this.stillGoing = false;

    return undefined;
  }

  public async _pause(): Promise<void> {
    this.isPaused = true;
    await this.routineContext.stopGripper();
  }

  public _resume(): void {
    this.isPaused = false;
  }

  public async _stop(): Promise<void> {
    this.stillGoing = false;

    // Don't reset `succeeded` here. RoutineRunner.playSteps calls stop() on
    // every step as soon as it finishes playing, so clearing the variable
    // makes it read `false` for every later step that checks it. `_play`
    // already sets it to `false` on entry, and constructStep resets variables
    // when the routine loads.
    await this.routineContext.stopGripper();
  }

  public getSelfTimeout(): number {
    return 10_000;
  }
}
